/**
 * Documents imprimés d'une hospitalisation (fiche, reçus de paiement).
 *
 * Fonctions pures : elles reçoivent des données déjà chargées (hospitalisation,
 * paiements, situation calculée par le serveur, utilisateur…) et renvoient les
 * options de imprimer() ({ titre, numero, date, sousTitre, corps, signature, format }).
 * Aucun appel au serveur ici : l'écran charge les données puis imprime.
 *
 * Tous les montants agrégés (montant, déjà payé, parts, reste, remboursement)
 * viennent de GET hospitalisations/{id}/situation.
 */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { e, fcfa, blocInfos, nomComplet } from '../impression'

dayjs.extend(utc)
dayjs.extend(timezone)

/** Date au format 02-10-2026 (« — » si absente), comme à l'écran. */
export function formatDate(date) {
  if (!date) return '—';
  return dayjs(String(date).substring(0, 10)).format('DD-MM-YYYY');
}

/** Date et heure de Bamako au format 02-10-2026 14:05 (« — » si absente). */
export function formatDateTime(date) {
  if (!date) return '—';
  return dayjs(date)
    .tz('Africa/Bamako')
    .format('DD-MM-YYYY HH:mm');
}

/**
 * Fiche d'hospitalisation A4.
 * @param {{ hospitalisation: object, paiements: Array, situation: object, user?: object }} donnees
 */
export function documentHospitalisationA4({ hospitalisation, paiements, situation, user }) {
  user = user || {};
  paiements = paiements || [];
  const patient = hospitalisation.patient || {};
  const chambre = hospitalisation.chambre || {};
  const lit = hospitalisation.lit || {};
  const typeChambre = chambre.type || {};

  // Contrats d'assurance utilisés (portés par les paiements)
  const paiementsAvecContrat = paiements.filter(p => p.contratAssurance);

  const reference = hospitalisation.numHospitalisation || `HOSP-${hospitalisation.id}`;
  // Situation financière calculée par le serveur (paiements annulés exclus)
  const s = situation || {};
  const montantCalcule = Number(s.montant || 0);
  const totalPaye = Number(s.dejaPaye || 0);
  const resteAPayer = Number(s.resteAPayer || 0);
  const remboursement = Number(s.remboursement || 0);
  const tarifParJour = Number(s.prixParJour || 0);
  const nombreJours = Number(s.jours || 0);
  const totalPatient = Number(s.dejaPayePatient || 0);
  const totalAssurance = Number(s.dejaPrisEnChargeAssurance || 0);

  const lignesContrats = paiementsAvecContrat.length
    ? paiementsAvecContrat.map(p => {
        const contrat = p.contratAssurance || {};
        return `
        <tr>
          <td>${e(contrat.assurance?.nom || '—')}</td>
          <td>${e(contrat.numeroAssure || '—')}</td>
          <td class="droite">${e(Number(contrat.tauxCouverture || 0))} %</td>
          <td class="droite">${fcfa(p.partAssurance)}</td>
        </tr>`;
      }).join('')
    : '<tr><td colspan="4" class="centre muted">Aucun contrat d\'assurance</td></tr>';

  const lignesPaiements = paiements.length
    ? paiements.map(p => {
        const contrat = p.contratAssurance || null;
        return `
        <tr>
          <td>${e(formatDateTime(p.datePaiement))}</td>
          <td>${e(p.modePaiement?.nom || '—')}</td>
          <td>${e(contrat?.assurance?.nom || 'Aucune')}</td>
          <td>${e(contrat?.numeroAssure || '—')}</td>
          <td class="droite">${contrat ? e(Number(contrat.tauxCouverture || 0)) + ' %' : '—'}</td>
          <td class="droite">${fcfa(p.montantBase)}</td>
          <td class="droite">${fcfa(p.montant)}</td>
          <td class="droite">${fcfa(p.partPatient)}</td>
          <td class="droite">${fcfa(p.partAssurance)}</td>
        </tr>`;
      }).join('')
    : '<tr><td colspan="9" class="centre muted">Aucun paiement</td></tr>';

  const corps = `
    <div class="section">Patient</div>
    ${blocInfos([
      ['Nom', nomComplet(patient)],
      ['Dossier', patient.numeroDossier || '—'],
      ['Téléphone', patient.telephone || '—'],
      ['Genre', patient.genre || '—'],
    ])}
    <div class="section">Hospitalisation</div>
    ${blocInfos([
      ['Date d\'entrée', formatDate(hospitalisation.dateEntree)],
      ['Sortie prévue', formatDate(hospitalisation.dateSortiePrevue)],
      ['Sortie réelle', hospitalisation.dateSortieReelle ? formatDate(hospitalisation.dateSortieReelle) : 'En cours'],
      ['Statut', hospitalisation.statut || '—'],
      ['Type', hospitalisation.typeHospitalisation?.libelle || '—'],
    ])}
    <div class="section">Chambre, lit et tarification</div>
    ${blocInfos([
      ['Chambre', chambre.numero || '—'],
      ['Lit', lit.numero || '—'],
      ['Type de chambre', typeChambre.nom || '—'],
      ['Tarif / jour', fcfa(tarifParJour)],
      ['Durée', `${nombreJours} jour${nombreJours > 1 ? 's' : ''}`],
      ['Total calculé', fcfa(montantCalcule)],
    ])}
    <div class="section">Contrat(s) d'assurance utilisé(s)</div>
    <table class="lignes">
      <thead><tr><th>Assurance</th><th>N° assuré</th><th class="droite">Taux de couverture</th><th class="droite">Part assurance</th></tr></thead>
      <tbody>${lignesContrats}</tbody>
    </table>
    <div class="section">Situation financière</div>
    <table class="totaux">
      <tr><td>Montant</td><td class="droite">${fcfa(montantCalcule)}</td></tr>
      <tr><td>Part assurance</td><td class="droite">${fcfa(totalAssurance)}</td></tr>
      <tr><td>Part patient</td><td class="droite">${fcfa(totalPatient)}</td></tr>
      <tr><td>Total payé</td><td class="droite">${fcfa(totalPaye)}</td></tr>
      ${remboursement > 0 ? `<tr><td>Remboursement</td><td class="droite">${fcfa(remboursement)}</td></tr>` : ''}
      <tr class="grand"><td>Reste à payer</td><td class="droite${resteAPayer > 0 ? ' anormal' : ''}">${fcfa(resteAPayer)}</td></tr>
    </table>
    <div class="section">Historique des paiements</div>
    <table class="lignes">
      <thead><tr><th>Date</th><th>Mode</th><th>Assurance</th><th>N° assuré</th><th class="droite">Taux</th><th class="droite">Base</th><th class="droite">Montant</th><th class="droite">Patient</th><th class="droite">Assurance</th></tr></thead>
      <tbody>${lignesPaiements}</tbody>
    </table>`;

  const agent = nomComplet(user);
  return {
    titre: "Fiche d'hospitalisation",
    numero: reference,
    date: formatDate(hospitalisation.dateEntree),
    sousTitre: hospitalisation.statut ? `Statut : ${hospitalisation.statut}` : '',
    corps,
    signature: { droite: `<strong>L'agent</strong>${agent ? '<br>' + e(agent) : ''}<br><span class="muted">Signature et cachet</span>` },
  };
}

/**
 * Fiche d'hospitalisation au format ticket (80 mm, aussi utilisé pour 58 mm).
 * @param {{ hospitalisation: object, paiements: Array, situation: object, user?: object }} donnees
 */
export function documentHospitalisationTicket({ hospitalisation, paiements, situation, user }) {
  user = user || {};
  paiements = paiements || [];
  const patient = hospitalisation.patient || {};
  const chambre = hospitalisation.chambre || {};
  const lit = hospitalisation.lit || {};
  const typeChambre = chambre.type || {};

  // Situation financière calculée par le serveur (paiements annulés exclus)
  const s = situation || {};
  const montantCalcule = Number(s.montant || 0);
  const totalPaye = Number(s.dejaPaye || 0);
  const resteAPayer = Number(s.resteAPayer || 0);
  const remboursement = Number(s.remboursement || 0);
  const tarifParJour = Number(s.prixParJour || 0);
  const nombreJours = Number(s.jours || 0);
  const totalPatient = Number(s.dejaPayePatient || 0);
  const totalAssurance = Number(s.dejaPrisEnChargeAssurance || 0);
  const paiementsAvecContrat = paiements.filter(p => p.contratAssurance);

  const assurances = paiementsAvecContrat.length
    ? paiementsAvecContrat.map(p => {
        const contrat = p.contratAssurance || {};
        return blocInfos([
          ['Assurance', contrat.assurance?.nom || '—'],
          ['N° assuré', contrat.numeroAssure || '—'],
          ['Taux', `${Number(contrat.tauxCouverture || 0)} %`],
          ['Part assurance', fcfa(p.partAssurance)],
          ['Part patient', fcfa(p.partPatient)],
        ], 1);
      }).join('')
    : '<div class="centre muted">Aucune assurance</div>';

  const agent = nomComplet(user);
  const corps = `
    <div class="section">Patient</div>
    ${blocInfos([
      ['Nom', nomComplet(patient) || '—'],
      ['Dossier', patient.numeroDossier || '—'],
      ['Téléphone', patient.telephone || '—'],
      ['Genre', patient.genre],
    ], 1)}
    <div class="section">Hospitalisation</div>
    ${blocInfos([
      ['ID', hospitalisation.id],
      ['Entrée', formatDate(hospitalisation.dateEntree)],
      ['Sortie prévue', formatDate(hospitalisation.dateSortiePrevue)],
      ['Sortie réelle', hospitalisation.dateSortieReelle ? formatDate(hospitalisation.dateSortieReelle) : 'En cours'],
      ['Statut', hospitalisation.statut || '—'],
      ['Type', hospitalisation.typeHospitalisation?.libelle],
    ], 1)}
    <div class="section">Chambre et lit</div>
    ${blocInfos([
      ['Chambre', chambre.numero || '—'],
      ['Type', typeChambre.nom || '—'],
      ['Lit', lit.numero || '—'],
      ['Tarif / jour', fcfa(tarifParJour)],
      ['Nombre de jours', `${nombreJours} jour${nombreJours > 1 ? 's' : ''}`],
    ], 1)}
    <div class="section">Assurance</div>
    ${assurances}
    <table class="totaux">
      <tr><td>Montant</td><td class="droite">${fcfa(montantCalcule)}</td></tr>
      <tr><td>Part patient</td><td class="droite">${fcfa(totalPatient)}</td></tr>
      <tr><td>Part assurance</td><td class="droite">${fcfa(totalAssurance)}</td></tr>
      <tr><td>Total payé</td><td class="droite">${fcfa(totalPaye)}</td></tr>
      ${remboursement > 0 ? `<tr><td>Remboursement</td><td class="droite">${fcfa(remboursement)}</td></tr>` : ''}
      <tr class="grand"><td>Reste</td><td class="droite">${fcfa(resteAPayer)}</td></tr>
    </table>
    <div class="centre" style="margin-top:8px">${agent ? `Agent : <strong>${e(agent)}</strong><br>` : ''}Merci pour votre confiance</div>`;

  return {
    titre: 'Hospitalisation',
    numero: hospitalisation.numHospitalisation || `HOSP-${hospitalisation.id}`,
    date: formatDate(hospitalisation.dateEntree),
    corps,
    format: 'ticket',
  };
}

/** Données communes aux reçus de paiement d'hospitalisation (A4 et ticket). */
export function donneesPaiementHospitalisation(paiement, hospitalisation, user) {
  const contrat = paiement.contratAssurance || null;
  return {
    patient: hospitalisation.patient || {},
    contrat,
    reference: paiement.ref || hospitalisation.numHospitalisation || `PAY-HOSP-${hospitalisation.id}`,
    date: formatDateTime(paiement.datePaiement || paiement.createdAt),
    taux: Number(contrat?.tauxCouverture || 0),
    montantBase: Number(paiement.montantBase ?? paiement.montant ?? 0),
    agent: nomComplet(user),
  };
}

/**
 * Reçu A4 d'un paiement d'hospitalisation.
 * @param {{ paiement: object, hospitalisation: object, user?: object }} donnees
 */
export function documentRecuPaiementHospitalisationA4({ paiement, hospitalisation, user }) {
  const d = donneesPaiementHospitalisation(paiement, hospitalisation, user);

  const corps = `
    ${blocInfos([
      ['Patient', nomComplet(d.patient) || '—'],
      ['Dossier', d.patient.numeroDossier || '—'],
      ['N° hospitalisation', hospitalisation.numHospitalisation || '—'],
      ['Mode de paiement', paiement.modePaiement?.nom || '—'],
      ['Date du paiement', d.date],
      ['Statut', '<span class="badge-ok">Payé</span>', true],
    ])}
    <table class="lignes">
      <thead><tr><th class="droite">Montant base</th><th class="droite">Montant payé</th><th>Assurance</th><th class="droite">Taux de couverture</th><th class="droite">Part assurance</th><th class="droite">Part patient</th></tr></thead>
      <tbody><tr>
        <td class="droite">${fcfa(d.montantBase)}</td>
        <td class="droite"><strong>${fcfa(paiement.montant)}</strong></td>
        <td>${d.contrat?.assurance?.nom ? `<strong>${e(d.contrat.assurance.nom)}</strong><br>N° ${e(d.contrat.numeroAssure || '—')}` : 'Aucune assurance'}</td>
        <td class="droite">${d.contrat ? e(d.taux) + ' %' : '—'}</td>
        <td class="droite">${fcfa(paiement.partAssurance)}</td>
        <td class="droite">${fcfa(paiement.partPatient)}</td>
      </tr></tbody>
    </table>
    ${d.contrat
      ? `<div class="section">Contrat d'assurance du patient</div>
         ${blocInfos([
           ['Assurance', d.contrat.assurance?.nom || '—'],
           ['N° assuré', d.contrat.numeroAssure || '—'],
           ['Taux de couverture', `${d.taux} %`],
         ])}`
      : '<div class="encadre"><strong>Paiement effectué intégralement par le patient.</strong></div>'}`;

  return {
    titre: 'Reçu de paiement — Hospitalisation',
    numero: d.reference,
    date: d.date,
    corps,
    signature: { droite: `<strong>L'agent</strong>${d.agent ? '<br>' + e(d.agent) : ''}<br><span class="muted">Signature et cachet</span>` },
  };
}

/**
 * Reçu ticket (80 mm, aussi utilisé pour 58 mm) d'un paiement d'hospitalisation.
 * @param {{ paiement: object, hospitalisation: object, user?: object }} donnees
 */
export function documentRecuPaiementHospitalisationTicket({ paiement, hospitalisation, user }) {
  const d = donneesPaiementHospitalisation(paiement, hospitalisation, user);

  const corps = `
    ${blocInfos([
      ['Patient', nomComplet(d.patient) || '—'],
      ['Dossier', d.patient.numeroDossier || '—'],
      ['Hospitalisation', hospitalisation.numHospitalisation || '—'],
    ], 1)}
    <div class="section">Paiement</div>
    ${blocInfos([
      ['Mode', paiement.modePaiement?.nom || '—'],
      ['Type', paiement.type || '—'],
      ['Montant base', fcfa(d.montantBase)],
      ['Part assurance', fcfa(paiement.partAssurance)],
      ['Part patient', fcfa(paiement.partPatient)],
    ], 1)}
    <div class="section">Assurance</div>
    ${d.contrat
      ? blocInfos([
          ['Assurance', d.contrat.assurance?.nom || '—'],
          ['N° assuré', d.contrat.numeroAssure || '—'],
          ['Couverture', `${d.taux} %`],
        ], 1)
      : '<div class="centre muted">Aucune assurance</div>'}
    <table class="totaux">
      <tr class="grand"><td>Total payé</td><td class="droite">${fcfa(paiement.montant)}</td></tr>
    </table>
    <div class="centre" style="margin-top:8px">${d.agent ? `Agent : <strong>${e(d.agent)}</strong><br>` : ''}Merci pour votre confiance</div>`;

  return {
    titre: 'Reçu de paiement',
    numero: d.reference,
    date: d.date,
    corps,
    format: 'ticket',
  };
}

export default {
  documentHospitalisationA4,
  documentHospitalisationTicket,
  donneesPaiementHospitalisation,
  documentRecuPaiementHospitalisationA4,
  documentRecuPaiementHospitalisationTicket,
  formatDate,
  formatDateTime,
}
